import type { SmartOLTOnuRow, SmartOLTStatusKind } from "../../../services/smartolt.service.ts"

export type PonFilter = "" | "gpon" | "epon"
export type StatusFilter = "" | SmartOLTStatusKind
export type SignalBand = "strong" | "medium" | "weak"
export type SortKey =
  | "name"
  | "sn"
  | "zone"
  | "signal"
  | "vlan"
  | "type"
  | "auth"
  | "mgmt"

export function displayName(row: SmartOLTOnuRow) {
  return row.smartolt_name || row.customer_name || row.onu_external_id || "—"
}

export function statusKind(row: SmartOLTOnuRow): SmartOLTStatusKind {
  if (row.status_kind) return row.status_kind
  const raw = String(row.status || "").trim().toLowerCase()
  if (raw === "online" || raw === "on") return "online"
  if (raw === "los" || raw.includes("fiber")) return "los"
  if (raw.includes("power") || raw.includes("dying")) return "power"
  return "offline"
}

export function parseSignal(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null
  const num = typeof value === "number" ? value : Number(String(value).replace(/[^\d.-]/g, ""))
  return Number.isFinite(num) ? num : null
}

export function signalBand(value: number | string | null | undefined): SignalBand | "" {
  const rx = parseSignal(value)
  if (rx == null) return ""
  if (rx > -27) return "strong"
  if (rx > -30) return "medium"
  return "weak"
}

export function formatSignal(value: number | string | null | undefined) {
  const rx = parseSignal(value)
  if (rx == null) return "-"
  return `${rx}dBm`
}

export function formatAuthDate(value?: string | null) {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function wanLabel(value?: string | null) {
  const text = String(value || "").toLowerCase()
  if (text.includes("bridg")) return "Bridging"
  if (text.includes("rout")) return "Routing"
  return value || ""
}

export function hasService(value: unknown) {
  if (value == null || value === false) return false
  const text = String(value).trim().toLowerCase()
  return text !== "" && text !== "-" && text !== "0" && text !== "none" && text !== "disabled" && text !== "off"
}

export function isServiceOn(value: unknown) {
  const text = String(value ?? "").trim().toLowerCase()
  return text.includes("enable") || text.includes("on") || text === "1" || text === "true" || text === "yes"
}

export function compareRows(a: SmartOLTOnuRow, b: SmartOLTOnuRow, key: SortKey, dir: "asc" | "desc") {
  const pick = (row: SmartOLTOnuRow): string | number => {
    if (key === "name") return displayName(row).toLowerCase()
    if (key === "sn") return String(row.sn || row.mac || "").toLowerCase()
    if (key === "zone") return String(row.zone_name || "").toLowerCase()
    if (key === "signal") return parseSignal(row.signal_rx) ?? (dir === "asc" ? 999 : -999)
    if (key === "vlan") return Number(row.vlan) || String(row.vlan || "")
    if (key === "type") return String(row.onu_type_name || "").toLowerCase()
    if (key === "auth") return String(row.auth_date || row.fetched_at || "")
    return String(row.mgmt_ip || "")
  }
  const left = pick(a)
  const right = pick(b)
  const result = left < right ? -1 : left > right ? 1 : 0
  return dir === "asc" ? result : -result
}
